<template>
  <v-container>
    <v-dialog
      :value="value"
      @input="$emit('input', $event.target.value)"
      fullscreen
      transition="dialog-bottom-transition"
    >
      <v-card color="primary">
        <v-toolbar color="primary">
          <v-toolbar-title>
            <h3>{{ isEdit ? "Editing Shipping Audit" : "New Shipping Audit" }}</h3>
          </v-toolbar-title>
          <v-spacer />
          <v-btn icon @click="reset();">
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-stepper v-model="currentStep" vertical class="elevation-0" v-if="value">
          <v-stepper-step step="1" :complete="currentStep > 1" editable>Enter Pickslip</v-stepper-step>
          <v-stepper-content step="1" class="mr-2">
            <v-form @submit.prevent="currentStep++">
              <v-card-text>
                <PickingSearch @picking-selected="handlePicking" v-if="!isEdit" />
                <v-text-field
                  v-if="isEdit"
                  v-show="!showNewSearch"
                  v-model="body.pickslip"
                  box
                  color="secondary"
                  prepend-inner-icon="search"
                  :readonly="true"
                  label="Picking Sheet"
                >
                  <v-btn icon slot="append" class="mt-0">
                    <v-icon @click="showNewSearch = true; body.pickslip = '';">close</v-icon>
                  </v-btn>
                </v-text-field>
                <PickingSearch @picking-selected="handlePicking" v-if="showNewSearch" />
              </v-card-text>

              <v-card-actions>
                <v-spacer />
                <v-btn @click="reset();" flat>Cancel</v-btn>
                <v-btn type="submit" flat color="secondary lighten-2">Continue</v-btn>
              </v-card-actions>
            </v-form>
          </v-stepper-content>

          <v-stepper-step step="2" :complete="currentStep > 2">Enter Products</v-stepper-step>
          <v-stepper-content step="2">
            <v-form @submit.prevent="createOrEdit()" class="mr-4" v-if="currentStep === 2">
              <v-card-actions>
                <v-spacer />
                <v-btn flat @click="addProduct()">Add Product</v-btn>
              </v-card-actions>
              <v-card-text v-for="(product, index) in totalProducts" :key="index">
                <div
                  v-if="value"
                  :class="index % 2 ? 'primary lighten-2 pa-2 elevation-5' : 'primary lighten-1 pa-2 elevation-5'"
                >
                  <v-text-field label="SKU" color="secondary" v-model="product.sku" />
                  <v-text-field
                    label="Quantity"
                    color="secondary"
                    v-model="product.quantity"
                    type="number"
                  />
                  <YesOrNo
                    v-if="value"
                    question="Audit Passed?"
                    :alternate="index % 2 ? false : true"
                    :index="index"
                    @data="handleQuestion"
                    :fillAnswer="totalProducts[index].responses"
                  />
                  <v-card-actions v-if="index != 0 || totalProducts.length > 1">
                    <v-spacer />
                    <v-btn flat @click="removeProduct(index)">Remove Product</v-btn>
                  </v-card-actions>
                </div>
              </v-card-text>
              <v-card-text>
                <v-text-field
                  v-model="body.remarks"
                  label="Use this field to add any additional comments to this audit"
                  color="secondary"
                  box
                  class="mt-5"
                ></v-text-field>
              </v-card-text>
              <v-card-actions>
                <v-spacer />
                <v-btn @click="currentStep--;" flat>Go Back</v-btn>
                <v-btn type="submit" flat color="secondary lighten-2">Submit</v-btn>
              </v-card-actions>
            </v-form>
          </v-stepper-content>
        </v-stepper>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import PickingSearch from "../../app/PickingSearch";
import YesOrNo from "../../app/YesOrNo";
import { detailedDiff } from "deep-object-diff";

export default {
  name: "Create",

  props: ["value"],

  components: {
    PickingSearch,
    YesOrNo
  },

  data() {
    return {
      isEdit: false,
      currentStep: 1,
      body: {
        auditor: {
          name: "",
          badgeID: ""
        },
        pickslip: "",
        products: [],
        remarks: "",
        totalProductCount: 0
      },
      totalProducts: [
        {
          sku: null,
          quantity: 1,
          responses: {
            yes: false,
            no: false,
            comments: ""
          }
        }
      ],
      showNewSearch: false
    };
  },

  watch: {
    edit(val) {
      if (val._id) {
        this.isEdit = true;
        this.body = {
          pickslip: val.pickslip,
          auditor: val.auditor,
          remarks: val.remarks
        };
        this.totalProducts = val.products;
      }
    },
    body: {
      handler(val) {
        if (val.pickslip === "" || val.pickslip === null) {
          this.totalProducts = [
            {
              sku: null,
              quantity: 1,
              responses: {
                yes: false,
                no: false,
                comments: ""
              }
            }
          ];
        }
      },
      deep: true
    }
  },

  computed: {
    ...mapGetters({
      user: "app/getUser",
      edit: "audits/getEditAudit",
      stockMove: "stockMove/getMove"
    })
  },

  methods: {
    ...mapActions({
      moveSearch: "stockMove/search",
      create: "audits/create",
      update: "audits/update"
    }),
    reset() {
      this.body = {
        auditor: {
          name: "",
          badgeID: ""
        },
        pickslip: "",
        products: [],
        remarks: ""
      };
      this.totalProducts = [
        {
          sku: null,
          quantity: 1,
          responses: {
            yes: false,
            no: false,
            comments: ""
          }
        }
      ];

      this.currentStep = 1;
      this.isEdit = false;
      this.showNewSearch = false;
      this.$emit("input", false);
      this.$store.commit("audits/setEditAudit", []);
      this.$store.dispatch("audits/getAllAudits", {
        rowsPerPage: 10,
        sortBy: "dateCreated",
        descending: true,
        page: 1,
        from: null,
        to: null,
        search: null,
        module: "shipping"
      });
    },
    createOrEdit() {
      if (this.body.pickslip === "") {
        this.alert("No Pickslip Selected!");
      } else if (this.setAndConfirmProducts()) {
        if (!this.isEdit) {
          this.body.auditor = {
            name: this.user.displayName,
            badgeID: this.user.employeeID
          };
          this.create({
            module: "shipping",
            body: this.body
          });
        } else {
          this.body.editedBy = this.user.displayName;
          this.body.changed = this.getChanged();
          this.update({
            module: "shipping",
            id: this.edit._id,
            body: this.body
          });
        }
        this.reset();
      }
    },
    addProduct(sku = null, qty = 1) {
      this.totalProducts.push({
        sku: sku,
        quantity: qty,
        responses: {
          yes: false,
          no: false,
          comments: ""
        }
      });
    },
    removeProduct(index) {
      for (let i = index; i < this.totalProducts.length; i++) {
        if (this.totalProducts[i + 1]) {
          this.totalProducts[i].responses = this.totalProducts[i + 1].responses;
        }
      }

      this.totalProducts.splice(index, 1);
    },
    handleQuestion(data) {
      this.totalProducts[data.index].responses = data.val;
    },
    handlePicking(data) {
      if (this.showNewSearch || !this.isEdit) {
        this.totalProducts = [];
      }
      data.move_lines.forEach(async moveID => {
        await this.moveSearch([
          {
            field: "id",
            operator: "=",
            value: moveID
          }
        ]);
        this.addProduct(this.stockMove[0].name, this.stockMove[0].ordered_qty);
      });
      this.body.pickslip = data.name;
      this.currentStep++;
    },
    setAndConfirmProducts() {
      let message = "";
      let productCount = 0;
      let correct = this.totalProducts.map(product => {
        productCount += parseInt(product.quantity);
        if (product.responses.no && !product.responses.comments) {
          message = "No chosen as an answer but no reason left!";
          return 1;
        } else if (!product.responses.no && !product.responses.yes) {
          message = "Some audit questions have not been answered!";
          return 1;
        } else if (product.sku === null || product.sku === "") {
          message = "A product sku cannot be empty!";
          return 1;
        } else if (product.quantity < 1) {
          message = "Quantity must be greater than 1!";
          return 1;
        } else {
          return 0;
        }
      });
      if (correct.includes(1)) {
        this.alert(message);
        return false;
      } else {
        this.body.products = this.totalProducts;
        this.body.totalProductCount = productCount;
        return true;
      }
    },
    getChanged() {
      let edit = this.edit;
      let body = this.body;
      let oldObj = {
        auditor: edit.auditor,
        pickslip: edit.pickslip,
        products: edit.products,
        remarks: edit.remarks,
        totalProductCount: edit.totalProductCount
      };
      let newObj = {
        auditor: body.auditor,
        pickslip: body.pickslip,
        products: body.products,
        remarks: body.remarks,
        totalProductCount: body.totalProductCount
      };

      return detailedDiff(oldObj, newObj);
    },
    alert(msg) {
      this.$store.dispatch("app/setSnackbar", {
        color: "red",
        text: msg
      });
    }
  }
};
</script>

<style>
</style>
